import React, { useState, useMemo, useEffect } from 'react';
import { useParams, Link, useNavigate, useSearchParams } from 'react-router-dom';
import { ArrowLeft, MapPin, Shield, AlertCircle, MessageSquare, Clock, Banknote } from 'lucide-react';
import { useQuery, useMutation } from '@apollo/client';
import toast from 'react-hot-toast';
import { GET_TOOL_BY_ID } from '../graphql/queries/getTool';
import { CREATE_TOOL_BOOKING } from '../graphql/mutations/createToolBooking';
import Button from '../components/ui/Button';
import DatePicker from '../components/ui/DatePicker';
import UserAvatar from '../components/ui/UserAvatar';
import { useAuth } from '../context/AuthContext';

const RequestBook: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user } = useAuth();
  
  const { data: toolData, loading: loadingTool } = useQuery(GET_TOOL_BY_ID, {
    variables: { 
      id,
      availabilityStartDate: new Date().toISOString().split('T')[0]
    },
    fetchPolicy: 'network-only',
  });
  
  const [createBooking] = useMutation(CREATE_TOOL_BOOKING);
  
  const tool = toolData?.tool;

  useEffect(() => {
    if (tool?.disabled) {
      toast.error('Dette værktøj er deaktiveret og kan ikke bookes');
      navigate(`/tool/${id}`, { replace: true });
    }
  }, [tool, navigate, id]);
  
  // Get dates from URL params
  const [startDate, setStartDate] = useState(searchParams.get('startDate') || '');
  const [endDate, setEndDate] = useState(searchParams.get('endDate') || '');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [dateValidationError, setDateValidationError] = useState<string>('');
  const [message, setMessage] = useState('');
  
  // Generate default message when data is available
  useEffect(() => {
    if (tool && startDate && endDate && user && !message) {
      const formatDate = (dateStr: string) => {
        const date = new Date(dateStr);
        return date.toLocaleDateString('da-DK', { day: 'numeric', month: 'long', year: 'numeric' });
      };
      
      const defaultMessage = `Hej,\n\nJeg vil gerne låne "${tool.name}" fra ${formatDate(startDate)} til ${formatDate(endDate)}.\n\nMed venlig hilsen\n${user.name || 'Bruger'}`;
      setMessage(defaultMessage);
    }
  }, [tool, startDate, endDate, user, message]);
  
  // Calculate all disabled dates for date pickers
  const disabledDates = useMemo(() => {
    if (!toolData?.tool) return [];
    
    const disabled: string[] = [];
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    // Generate dates for next 2 years to check
    const endDate = new Date();
    endDate.setFullYear(endDate.getFullYear() + 2);
    
    const availabilityPeriods = toolData.tool.toolAvailabilityPeriods || [];
    const bookings = toolData.tool.relevantToolBookings || [];
    
    const currentDate = new Date(today);
    while (currentDate <= endDate) {
      const dateStr = currentDate.toISOString().split('T')[0];
      
      // Find ALL matching availability periods for this date
      const matchingPeriods = availabilityPeriods.filter((period: any) => {
        const periodStartStr = period.startDate;
        const periodEndStr = period.endDate;
        
        return dateStr >= periodStartStr && (!periodEndStr || dateStr <= periodEndStr);
      });
      
      // Check if there are any bookings for this date
      const hasBooking = bookings.some((booking: any) => {
        if (booking.status === 'CANCELLED') return false;
        return dateStr >= booking.fromDate && dateStr <= booking.toDate;
      });
      
      // Determine if date should be disabled
      let shouldDisable = false;
      
      if (hasBooking) {
        // If there's a booking, always disable
        shouldDisable = true;
      } else if (matchingPeriods.length === 0) {
        // No availability period defined = disable
        shouldDisable = true;
      } else {
        // Multiple periods may match - prioritize most specific
        // Specific periods (with endDate) override open-ended ones
        const specificPeriods = matchingPeriods.filter((p: any) => p.endDate);
        const periodToUse = specificPeriods.length > 0 
          ? specificPeriods[specificPeriods.length - 1]
          : matchingPeriods[matchingPeriods.length - 1];
        
        // Disable if the chosen period is not available
        shouldDisable = !periodToUse.available;
      }
      
      if (shouldDisable) {
        disabled.push(dateStr);
      }
      
      currentDate.setDate(currentDate.getDate() + 1);
    }
    
    return disabled;
  }, [toolData]);

  // Check if date range is valid
  const validateDateRange = (start: string, end: string): string => {
    if (!start || !end) return '';
    
    const startDate = new Date(start);
    const endDate = new Date(end);
    
    if (startDate > endDate) {
      return 'Slutdato skal være efter startdato';
    }
    
    // Check each day in the range
    const currentDate = new Date(start);
    while (currentDate <= endDate) {
      const dateStr = currentDate.toISOString().split('T')[0];
      if (disabledDates.includes(dateStr)) {
        return `Værktøjet er ikke tilgængeligt den ${currentDate.toLocaleDateString('da-DK')}`;
      }
      currentDate.setDate(currentDate.getDate() + 1);
    }
    
    return '';
  };

  // Handle date changes with validation
  const handleStartDateChange = (newStartDate: string) => {
    const shouldAdjustEndDate =
      !!endDate &&
      !!newStartDate &&
      new Date(newStartDate).getTime() > new Date(endDate).getTime();

    const adjustedEndDate = shouldAdjustEndDate ? newStartDate : endDate;

    setStartDate(newStartDate);
    if (shouldAdjustEndDate) {
      setEndDate(newStartDate);
    }

    const error = validateDateRange(newStartDate, adjustedEndDate);
    setDateValidationError(error);
  };

  const handleEndDateChange = (newEndDate: string) => {
    setEndDate(newEndDate);
    const error = validateDateRange(startDate, newEndDate);
    setDateValidationError(error);
  };
  
  // Calculate rental duration and costs
  const days = startDate && endDate 
    ? Math.ceil((new Date(endDate).getTime() - new Date(startDate).getTime()) / (1000 * 60 * 60 * 24)) + 1
    : 0;
  
  const pricePerDay = (tool?.pricePerDayCents ?? 0) / 100.0;
  const rentalCost = days * pricePerDay;
  const totalCost = rentalCost;
  
  // Format price in Danish format (comma separator, only significant decimals)
  const formatPrice = (price: number): string => {
    const hasDecimals = price % 1 !== 0;
    return hasDecimals 
      ? price.toFixed(2).replace('.', ',')
      : price.toFixed(0);
  };
  
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (tool?.disabled) {
      setError('Dette værktøj er deaktiveret og kan ikke bookes');
      return;
    }
    
    if (!startDate || !endDate) {
      setError('Vælg venligst lejedatoer');
      return;
    }
    
    if (days < 1) {
      setError('Ugyldig datoperiode');
      return;
    }
    
    if (!message.trim()) {
      setError('Skriv venligst en besked til ejeren');
      return;
    }
    
    if (!user?.id) {
      setError('Du skal være logget ind for at booke');
      return;
    }
    
    setIsLoading(true);
    
    try {
      const result = await createBooking({
        variables: {
          input: {
            toolId: id,
            fromDate: startDate,
            toDate: endDate,
            messageToOwner: message,
            requestingUserId: user.id,
          },
        },
      });
      
      if (result.data?.createToolBooking?.errors?.length > 0) {
        setError(result.data.createToolBooking.errors.join(', '));
      } else {
        toast.success('Bookingforespørgsel sendt! Ejeren vil blive notificeret.');
        navigate('/dashboard?tab=borrow_bookings');
      }
    } catch (err) {
      setError('Kunne ikke sende bookingforespørgsel. Prøv venligst igen.');
    } finally {
      setIsLoading(false);
    }
  };
  
  if (loadingTool) {
    return (
      <div className="container mx-auto px-4 py-8 sm:px-6 lg:px-8">
        <div className="text-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Indlæser værktøj...</p>
        </div>
      </div>
    );
  }

  if (!tool) {
    return (
      <div className="container mx-auto px-4 py-8 sm:px-6 lg:px-8">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900">Værktøj ikke fundet</h2>
          <p className="mt-2 text-gray-600">Værktøjet du leder efter findes ikke.</p>
          <Link to="/browse" className="mt-4 inline-block">
            <Button variant="primary">Find værktøj</Button>
          </Link>
        </div>
      </div>
    );
  }

  if (tool.disabled) {
    return null;
  }

  return (
    <div className="container mx-auto px-4 py-8 sm:px-6 lg:px-8">
      <button onClick={() => navigate(-1)} className="flex items-center text-green-600 mb-8 hover:text-green-700">
        <ArrowLeft className="h-5 w-5 mr-2" />
        <span>Tilbage til værktøj</span>
      </button>
      
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Booking Form */}
        <div>
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-8">
            <h1 className="text-2xl font-bold text-gray-900 mb-6">Booking anmodning</h1>
            
            <form onSubmit={handleSubmit}>
              {/* Tool Summary */}
              <div className="flex items-center mb-6 pb-6 border-b border-gray-200">
                <img 
                  src={tool.primaryImageUrl} 
                  alt={tool.name}
                  className="h-20 w-20 rounded object-cover"
                />
                <div className="ml-4">
                  <h2 className="text-lg font-medium text-gray-900">{tool.name}</h2>
                  <div className="flex items-center text-sm text-gray-500 mt-1">
                    <MapPin className="h-4 w-4 mr-1" />
                    <span>{tool.city}{tool.postalCode ? `, ${tool.postalCode}` : ''}</span>
                  </div>
                </div>
              </div>
              
              {/* Rental Dates */}
              <div className="mb-6">
                <h3 className="text-lg font-medium text-gray-900 mb-4">Lejeperiode</h3>
                <div className="grid grid-cols-2 gap-4">
                  <DatePicker
                    id="start-date"
                    label="Startdato"
                    value={startDate}
                    onChange={handleStartDateChange}
                    minDate={new Date().toISOString().split('T')[0]}
                    disabledDates={disabledDates}
                    error={!!dateValidationError}
                  />
                  <DatePicker
                    id="end-date"
                    label="Slutdato"
                    value={endDate}
                    onChange={handleEndDateChange}
                    minDate={startDate || new Date().toISOString().split('T')[0]}
                    disabledDates={disabledDates}
                    error={!!dateValidationError}
                  />
                </div>
                {dateValidationError && (
                  <div className="mt-3 p-3 bg-red-50 border border-red-200 rounded-md flex items-start">
                    <AlertCircle className="h-5 w-5 text-red-600 mr-2 flex-shrink-0 mt-0.5" />
                    <p className="text-sm text-red-600">{dateValidationError}</p>
                  </div>
                )}
              </div>
              
              {/* Message to Owner */}
              <div className="mb-6">
                <label htmlFor="message" className="flex items-center text-lg font-medium text-gray-900 mb-4">
                  <MessageSquare className="h-5 w-5 mr-2" />
                  Besked til ejer
                </label>
                <textarea
                  id="message"
                  rows={8}
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent resize-none"
                  placeholder="Skriv en besked til ejeren..."
                />
              </div>
              
              {error && (
                <div className="mb-6 rounded-md bg-red-50 p-4">
                  <div className="flex">
                    <AlertCircle className="h-5 w-5 text-red-400" />
                    <div className="ml-3">
                      <h3 className="text-sm font-medium text-red-800">{error}</h3>
                    </div>
                  </div>
                </div>
              )}
              
              <Button
                type="submit"
                variant="primary"
                fullWidth
                isLoading={isLoading}
              >
                Bekræft booking
              </Button>
            </form>
          </div>
        </div>
        
        {/* Price Breakdown and Info */}
        <div>
          {/* Price Breakdown */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-8">
            <h2 className="text-lg font-medium text-gray-900 mb-4">Pris detaljer</h2>
            <div className="space-y-3">
              <div className="flex justify-between text-sm">
                <span className="text-gray-600">{formatPrice(pricePerDay)} kr. × {days} dag(e)</span>
                <span className="font-medium">{formatPrice(rentalCost)} kr.</span>
              </div>
              <div className="pt-3 border-t border-gray-200">
                <div className="flex justify-between">
                  <span className="font-medium text-gray-900">Total</span>
                  <span className="font-medium text-gray-900">{formatPrice(totalCost)} kr.</span>
                </div>
              </div>
            </div>
          </div>
          
          {/* Owner Info */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-8">
            <h2 className="text-lg font-medium text-gray-900 mb-4">Mød ejeren</h2>
            <Link 
              to={`/profile/${tool.owner?.id}?returnTo=${encodeURIComponent(`/tool/${id}/book?${searchParams.toString()}`)}`} 
              className="flex items-center hover:bg-gray-50 p-2 rounded-lg transition-colors -m-2"
            >
              <UserAvatar user={tool.owner} />
              <div className="ml-4">
                <h3 className="text-sm font-medium text-gray-900">{tool.owner?.name}</h3>
                {tool.owner?.createdAt && (
                  <p className="text-sm text-gray-500 flex items-center">
                    <Clock className="h-3 w-3 mr-1" />
                    Medlem siden {new Date(tool.owner.createdAt).getFullYear()}
                  </p>
                )}
              </div>
            </Link>
          </div>
          
          {/* Payment Info */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <div className="flex items-center text-green-600 mb-4">
              <Banknote className="h-6 w-6 mr-2" />
              <h2 className="text-lg font-medium text-gray-900">Betaling</h2>
            </div>
            <p className="text-sm text-gray-600 mb-4">
              Betaling og opkrævning af lejebeløbet sker direkte mellem dig og udlejeren. 
              Skulle der opstå uenigheder omkring betalingen, står DoMore klar til at hjælpe med at afklare situationen.
            </p>
          </div>

          {/* Insurance Info */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mt-8">
            <div className="flex items-center text-green-600 mb-4">
              <Shield className="h-6 w-6 mr-2" />
              <h2 className="text-lg font-medium text-gray-900">Forsikringsdækning</h2>
            </div>
            <p className="text-sm text-gray-600 mb-4">
              Alle udlån inkluderer en grundlæggende forsikring for skader op til 2.500 kr. 
              Alle skader skal rapporteres inden for 48 timer efter returnering for at være dækket.
            </p>
            <Link to="/insurance" className="text-sm text-green-600 hover:text-green-700 font-medium">
              Læs mere om dækning
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RequestBook;